import { Component } from '@theme/component';
import { ThemeEvents, QuantitySelectorUpdateEvent } from '@theme/events';
import { morph } from '@theme/morph';
import { onAnimationEnd } from '@theme/utilities';
import { StandardEvents, ProductSelectEvent, CartLinesUpdateEvent, CartErrorEvent } from '@shopify/events';

/**
 * Dafodila customisation (Sticky Button Claude):
 * - The bar is visible from page load to the end of the page (no scroll trigger).
 * - Quantity stepper inside the bar, kept in sync with the main product-page quantity selector
 *   (the main selector stays the single source of truth).
 * - Buy Now triggers the main page Buy Now (Shiprocket headless checkout), so checkout flow,
 *   variant and quantity are identical to the main button.
 * - "Added to cart successfully" toast under the header cart icon after a successful add to cart
 *   (from the main Add to cart button or the sticky one).
 */

/**
 * @typedef {HTMLElement & {
 *   source: Element,
 *   destination: Element,
 *   useSourceSize: string | boolean
 * }} FlyToCart
 */

/**
 * @typedef {Object} StickyAddToCartRefs
 * @property {HTMLElement} stickyBar - The floating bar container
 * @property {HTMLButtonElement} addToCartButton - Sticky bar's button
 * @property {HTMLElement} quantityDisplay - Quantity display container
 * @property {HTMLElement} quantityNumber - Quantity number element
 * @property {HTMLImageElement} productImage - Product image element
 */

const TOAST_MESSAGE = 'Added to cart successfully';
const TOAST_DURATION = 3000;

/**
 * @extends {Component<StickyAddToCartRefs>}
 */
class StickyAddToCartComponent extends Component {
  requiredRefs = ['stickyBar', 'addToCartButton', 'quantityDisplay', 'quantityNumber'];

  /** @type {number | undefined} */
  #resetTimeout;

  /** @type {AbortController} */
  #abortController = new AbortController();

  /** @type {HTMLButtonElement | null} */
  #targetAddToCartButton = null;

  /** @type {number} */
  #currentQuantity = 1;

  /** @type {ResizeObserver | null} */
  #resizeObserver = null;

  /** @type {boolean} */
  #addPending = false;

  /** @type {number | undefined} */
  #addPendingTimeout;

  /** @type {HTMLElement | null} */
  #toast = null;

  /** @type {number | undefined} */
  #toastTimeout;

  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#abortController;

    const productForm = this.#getProductForm();
    if (productForm) {
      this.#targetAddToCartButton = productForm.querySelector('[ref="addToCartButton"]');
      // Any add to cart submit (main button or sticky puppet click) marks an add as pending.
      productForm.addEventListener('submit', this.#handleFormSubmit, { capture: true, signal });
    }

    const target = this.closest('.shopify-section');
    target?.addEventListener(StandardEvents.productSelect, this.#handleProductSelect, { signal });

    document.addEventListener(StandardEvents.cartLinesUpdate, this.#handleCartLinesUpdate, { signal });
    document.addEventListener(StandardEvents.cartError, this.#handleCartError, { signal });
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#handleQuantityUpdate, { signal });

    this.#getInitialQuantity();

    customElements.whenDefined('quantity-selector-component').then(() => {
      if (signal.aborted) return;
      this.#syncFromMainSelector();
    });

    // Always visible: show the bar right away and reserve space at the bottom of the page for it.
    this.#setupBottomOffset();
    requestAnimationFrame(() => this.#showStickyBar());
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController.abort();
    this.#resizeObserver?.disconnect();
    document.documentElement.style.removeProperty('--sticky-atc-offset');
    if (this.#toastTimeout) clearTimeout(this.#toastTimeout);
    if (this.#addPendingTimeout) clearTimeout(this.#addPendingTimeout);
    this.#toast?.remove();
  }

  /**
   * Keeps a CSS variable with the bar height so page content (footer) is never hidden behind the bar.
   */
  #setupBottomOffset() {
    const { stickyBar } = this.refs;
    if (!stickyBar || typeof ResizeObserver === 'undefined') return;

    const update = () => {
      const height = stickyBar.getBoundingClientRect().height;
      document.documentElement.style.setProperty('--sticky-atc-offset', `${Math.round(height)}px`);
    };

    this.#resizeObserver = new ResizeObserver(update);
    this.#resizeObserver.observe(stickyBar);
    update();
  }

  // Public action handlers
  /**
   * Handles the add to cart button click in the sticky bar
   */
  handleAddToCartClick = async () => {
    if (!this.#targetAddToCartButton) return;
    this.#targetAddToCartButton.dataset.puppet = 'true';
    this.#targetAddToCartButton.click();
    const cartIcon = document.querySelector('.header-actions__cart-icon');

    if (this.refs.addToCartButton.dataset.added !== 'true') {
      this.refs.addToCartButton.dataset.added = 'true';
    }

    if (this.#resetTimeout) clearTimeout(this.#resetTimeout);

    // No fly animation when the image is hidden (mobile) or missing; just reset the added state.
    const image = this.refs.productImage;
    if (!cartIcon || !this.refs.addToCartButton || !image || image.offsetParent === null) {
      this.#resetTimeout = setTimeout(() => {
        this.refs.addToCartButton.removeAttribute('data-added');
      }, 1500);
      return;
    }

    const flyToCartElement = /** @type {FlyToCart} */ (document.createElement('fly-to-cart'));

    flyToCartElement.classList.add('fly-to-cart--sticky');
    flyToCartElement.style.setProperty('background-image', `url(${image.src})`);
    flyToCartElement.useSourceSize = 'true';
    flyToCartElement.source = image;
    flyToCartElement.destination = cartIcon;

    document.body.appendChild(flyToCartElement);

    await onAnimationEnd([this.refs.addToCartButton, flyToCartElement]);
    this.#resetTimeout = setTimeout(() => {
      this.refs.addToCartButton.removeAttribute('data-added');
    }, 800);
  };

  /**
   * Sticky stepper: decrease quantity (delegates to the main quantity selector)
   */
  handleQuantityMinus = () => {
    this.#stepQuantity(-1);
  };

  /**
   * Sticky stepper: increase quantity (delegates to the main quantity selector)
   */
  handleQuantityPlus = () => {
    this.#stepQuantity(1);
  };

  /**
   * Sticky Buy Now: triggers the main page Buy Now button (Shiprocket headless checkout).
   * @param {Event} [event]
   */
  handleBuyNowClick = (event) => {
    event?.preventDefault?.();

    const target = this.#getMainBuyNowButton();
    if (!target) {
      this.#scrollToBuyButtons();
      return;
    }

    target.click();
  };

  /**
   * Marks an add to cart as pending when the product form is submitted
   */
  #handleFormSubmit = () => {
    this.#addPending = true;
    if (this.#addPendingTimeout) clearTimeout(this.#addPendingTimeout);
    // Safety net: forget the pending add if no cart event arrives
    this.#addPendingTimeout = setTimeout(() => {
      this.#addPending = false;
    }, 10000);
  };

  /**
   * Cart updated: reset puppet flag and, if an add was pending, show the toast once the request succeeds
   * @param {CartLinesUpdateEvent} event
   */
  #handleCartLinesUpdate = (event) => {
    const finish = () => {
      if (this.#targetAddToCartButton) {
        this.#targetAddToCartButton.dataset.puppet = 'false';
      }
    };

    const showIfPending = () => {
      if (!this.#addPending) return;
      this.#addPending = false;
      if (this.#addPendingTimeout) clearTimeout(this.#addPendingTimeout);
      this.#showToast();
    };

    if ('promise' in event && event.promise instanceof Promise) {
      event.promise
        .then(() => {
          // Let a possible cart error event run first
          setTimeout(showIfPending, 0);
        })
        .catch(() => {
          this.#addPending = false;
        })
        .finally(finish);
    } else {
      finish();
      showIfPending();
    }
  };

  /**
   * Cart error: no success toast
   */
  #handleCartError = () => {
    this.#addPending = false;
    if (this.#targetAddToCartButton) {
      this.#targetAddToCartButton.dataset.puppet = 'false';
    }
  };

  /**
   * Shows the "Added to cart successfully" toast just below the header cart icon
   */
  #showToast() {
    if (!this.#toast) {
      const toast = document.createElement('div');
      toast.className = 'dafodila-cart-toast';
      toast.setAttribute('role', 'status');
      toast.setAttribute('aria-live', 'polite');
      toast.innerHTML =
        '<span class="dafodila-cart-toast__icon" aria-hidden="true">' +
        '<svg viewBox="0 0 20 20" width="20" height="20" fill="none"><circle cx="10" cy="10" r="10" fill="#1a7f37"/>' +
        '<path d="M6 10.3l2.6 2.6L14.2 7.3" stroke="#fff" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>' +
        '</span><span class="dafodila-cart-toast__text"></span>';
      const text = toast.querySelector('.dafodila-cart-toast__text');
      if (text) text.textContent = TOAST_MESSAGE;
      document.body.appendChild(toast);
      this.#toast = toast;
    }

    const toast = this.#toast;

    // Position under the cart icon when it is visible, otherwise top centre
    const cartIcon = document.querySelector('.header-actions__cart-icon');
    const rect = cartIcon?.getBoundingClientRect();
    const iconVisible = rect && rect.width > 0 && rect.bottom > 0 && rect.top < window.innerHeight;

    if (iconVisible && rect) {
      toast.style.top = `${Math.round(rect.bottom + 10)}px`;
      toast.style.right = `${Math.max(12, Math.round(window.innerWidth - rect.right))}px`;
      toast.style.left = 'auto';
      toast.dataset.anchored = 'true';
    } else {
      toast.style.top = '16px';
      toast.style.left = '';
      toast.style.right = '';
      toast.dataset.anchored = 'false';
    }

    toast.dataset.visible = 'false';
    // Force reflow so the slide-in animation restarts on every add
    void toast.offsetWidth;
    toast.dataset.visible = 'true';

    if (this.#toastTimeout) clearTimeout(this.#toastTimeout);
    this.#toastTimeout = setTimeout(() => {
      toast.dataset.visible = 'false';
    }, TOAST_DURATION);
  }

  /**
   * Handles product select events (variant selected and updated)
   * @param {ProductSelectEvent} event - The product select event
   */
  #handleProductSelect = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;

    const { optionValueId } = event.detail ?? {};
    if (optionValueId) {
      this.dataset.currentVariantId = optionValueId;
    }

    event.promise
      .then(({ detail }) => {
        if (!detail?.html) return;

        const { html, productId, resource: variant } = detail;

        if (productId && productId !== this.dataset.productId) return;

        const newStickyAddToCart = /** @type {HTMLElement | null} */ (html.querySelector('sticky-add-to-cart'));
        if (!newStickyAddToCart) return;

        const newStickyBar = newStickyAddToCart.querySelector('[ref="stickyBar"]');
        if (!newStickyBar) return;

        const variantAvailable = newStickyAddToCart.dataset.variantAvailable;

        morph(this.refs.stickyBar, newStickyBar, { childrenOnly: true });

        // The bar is always visible
        this.refs.stickyBar.setAttribute('data-stuck', 'true');
        this.dataset.variantAvailable = variantAvailable;

        if (variant && variant.id) {
          this.dataset.currentVariantId = variant.id;
        }

        const productForm = this.#getProductForm();
        if (productForm) {
          this.#targetAddToCartButton = productForm.querySelector('[ref="addToCartButton"]');
        }

        if (variant == null) {
          this.#handleVariantUnavailable();
        }

        this.#syncFromMainSelector();
        requestAnimationFrame(() => this.#syncFromMainSelector());
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[sticky-add-to-cart] Event promise rejected:', error);
      });
  };

  /**
   * Updates the variant title based on selected options when the variant is unavailable
   */
  #handleVariantUnavailable = () => {
    this.dataset.currentVariantId = '';
    const variantTitleElement = this.querySelector('.sticky-add-to-cart__variant');
    const productId = this.dataset.productId;
    const variantPicker = document.querySelector(`variant-picker[data-product-id="${productId}"]`);
    if (!variantTitleElement || !variantPicker) return;

    const selectedOptions = Array.from(variantPicker.querySelectorAll('input:checked'))
      .map((option) => /** @type {HTMLInputElement} */ (option).value)
      .filter((value) => value !== '')
      .join(' / ');
    if (!selectedOptions) return;
    variantTitleElement.textContent = selectedOptions;
  };

  /**
   * Handles quantity selector update events
   * @param {QuantitySelectorUpdateEvent} event - The quantity update event
   */
  #handleQuantityUpdate = (event) => {
    if (event.detail.cartLine) return;

    this.#currentQuantity = event.detail.quantity;
    this.#updateButtonText();
  };

  /**
   * Shows the sticky bar
   */
  #showStickyBar() {
    const { stickyBar } = this.refs;
    stickyBar.dataset.stuck = 'true';
    this.#syncFromMainSelector();
  }

  /**
   * Gets the product form element
   * @returns {HTMLElement | null}
   */
  #getProductForm() {
    const productId = this.dataset.productId;
    if (!productId) return null;

    const sectionElement = this.closest('.shopify-section');
    if (!sectionElement) return null;

    const sectionId = sectionElement.id.replace('shopify-section-', '');
    return document.querySelector(
      `#shopify-section-${sectionId} product-form-component[data-product-id="${productId}"]`
    );
  }

  /**
   * Gets the main product-page quantity selector component
   * @returns {any}
   */
  #getMainQuantitySelector() {
    const productForm = this.#getProductForm();
    if (!productForm) return null;
    return productForm.querySelector('quantity-selector-component');
  }

  /**
   * Steps the main quantity selector (respects min / max / increment rules)
   * @param {number} direction - 1 or -1
   */
  #stepQuantity(direction) {
    const selector = this.#getMainQuantitySelector();
    if (!selector || typeof selector.updateQuantity !== 'function') return;

    selector.updateQuantity(direction);
    this.#syncFromMainSelector();
  }

  /**
   * Reads the quantity from the main selector and updates the sticky UI
   */
  #syncFromMainSelector() {
    const selector = this.#getMainQuantitySelector();
    const input = /** @type {HTMLInputElement | null} */ (selector?.querySelector('input[ref="quantityInput"]') ?? null);

    if (input) {
      const value = parseInt(input.value, 10);
      if (!Number.isNaN(value) && value > 0) {
        this.#currentQuantity = value;
      }
    }

    this.#updateButtonText();
  }

  /**
   * Finds the main Buy Now button (Shiprocket headless checkout, with Shopify's dynamic checkout as fallback)
   * @returns {HTMLElement | null}
   */
  #getMainBuyNowButton() {
    const productForm = this.#getProductForm();
    if (!productForm) return null;

    const scope = productForm.querySelector('.accelerated-checkout-block') ?? productForm;

    return /** @type {HTMLElement | null} */ (
      scope.querySelector('button.sr-headless-checkout:not([disabled])') ??
        scope.querySelector('.shopify-payment-button__button:not([disabled])')
    );
  }

  /**
   * Scrolls the main buy buttons into view
   */
  #scrollToBuyButtons() {
    const block = this.#getProductForm()?.closest('.buy-buttons-block');
    block?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }

  /**
   * Gets the initial quantity from the data attribute
   */
  #getInitialQuantity() {
    this.#currentQuantity = parseInt(this.dataset.initialQuantity || '1') || 1;
    this.#updateButtonText();
  }

  /**
   * Updates the sticky stepper and button states
   */
  #updateButtonText() {
    const { addToCartButton, quantityDisplay, quantityNumber } = this.refs;

    const available = !addToCartButton.disabled;

    quantityNumber.textContent = this.#currentQuantity.toString();
    quantityDisplay.style.display = 'none';

    const valueElement = this.querySelector('[ref="stickyQuantityValue"]');
    if (valueElement) valueElement.textContent = this.#currentQuantity.toString();

    const selector = this.#getMainQuantitySelector();
    const mainMinus = /** @type {HTMLButtonElement | null} */ (selector?.querySelector('[ref="minusButton"]') ?? null);
    const mainPlus = /** @type {HTMLButtonElement | null} */ (selector?.querySelector('[ref="plusButton"]') ?? null);

    const stickyMinus = /** @type {HTMLButtonElement | null} */ (this.querySelector('[ref="stickyMinus"]'));
    const stickyPlus = /** @type {HTMLButtonElement | null} */ (this.querySelector('[ref="stickyPlus"]'));

    if (stickyMinus) {
      stickyMinus.disabled = !available || (mainMinus ? mainMinus.disabled : this.#currentQuantity <= 1);
    }
    if (stickyPlus) {
      stickyPlus.disabled = !available || Boolean(mainPlus?.disabled);
    }
  }
}

if (!customElements.get('sticky-add-to-cart')) {
  customElements.define('sticky-add-to-cart', StickyAddToCartComponent);
}
